import { Tournament, Player, Round, MatchPlay } from '../types';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  version: number;
}

interface CacheConfig {
  maxAge: number;  // Maximum age in milliseconds
  version: number; // Cache version for breaking changes
  maxRequests?: number; // Maximum requests per interval
  interval?: number;    // Time interval for rate limiting in milliseconds
}

const CACHE_CONFIG: Record<string, CacheConfig> = {
  tournament: { 
    maxAge: 5 * 60 * 1000,  // 5 minutes
    version: 1,
    maxRequests: 1000,
    interval: 60 * 1000
  },
  players: { 
    maxAge: 5 * 60 * 1000,  // 5 minutes
    version: 1,
    maxRequests: 1000,
    interval: 60 * 1000
  },
  matches: { 
    maxAge: 2 * 60 * 1000,  // 2 minutes
    version: 1,
    maxRequests: 1000,
    interval: 60 * 1000
  },
  courses: {
    maxAge: 24 * 60 * 60 * 1000, // 24 hours
    version: 1
  }
};

export class CacheManager {
  private static instance: CacheManager;
  private cache: Map<string, CacheEntry<any>>;
  private memoryCache: Map<string, any>;
  private lastAccess: Map<string, number>;
  private requestCounts: Map<string, { count: number; resetTime: number }>;
  private readonly MEMORY_CACHE_TTL = 30 * 1000; // 30 seconds memory cache

  private constructor() {
    this.cache = new Map();
    this.memoryCache = new Map();
    this.lastAccess = new Map();
    this.requestCounts = new Map();
    this.startCleanupInterval();
    
    // Add automatic cache validation on startup
    this.validateCacheOnStartup();
  }

  static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  private startCleanupInterval() {
    setInterval(() => {
      const now = Date.now();
      // Clean up memory cache
      this.lastAccess.forEach((timestamp, key) => {
        if (now - timestamp > this.MEMORY_CACHE_TTL) {
          this.memoryCache.delete(key);
          this.lastAccess.delete(key);
        }
      });
      // Clean up rate limiting counters
      this.requestCounts.forEach((data, key) => {
        if (now >= data.resetTime) {
          this.requestCounts.delete(key);
        }
      });
    }, 60000); // Clean up every minute
  }

  private validateCacheOnStartup() {
    // Clear any invalid or corrupted cache entries
    for (const [key, entry] of this.cache.entries()) {
      const config = CACHE_CONFIG[key];
      if (!config || entry.version !== config.version) {
        this.clear(key);
        continue;
      }

      // Clear expired entries
      if (Date.now() - entry.timestamp > config.maxAge) {
        this.clear(key);
      }
    }

    // Clear memory cache on startup
    this.memoryCache.clear();
    this.lastAccess.clear();
  }

  isRateLimited(key: string): boolean {
    const config = CACHE_CONFIG[key];
    if (!config?.maxRequests || !config?.interval) return false;

    const now = Date.now();
    const requestCount = this.requestCounts.get(key);

    if (!requestCount || now >= requestCount.resetTime) {
      this.requestCounts.set(key, { count: 1, resetTime: now + config.interval });
      return false;
    }

    return requestCount.count >= config.maxRequests;
  }

  getRateLimitInfo(key: string): { remaining: number; resetIn: number } | null {
    const config = CACHE_CONFIG[key];
    if (!config?.maxRequests || !config?.interval) return null;

    const requestCount = this.requestCounts.get(key);
    if (!requestCount) {
      return {
        remaining: config.maxRequests,
        resetIn: 0
      };
    }

    return {
      remaining: Math.max(0, config.maxRequests - requestCount.count),
      resetIn: Math.max(0, requestCount.resetTime - Date.now())
    };
  }

  private updateRequestCount(key: string): boolean {
    const config = CACHE_CONFIG[key];
    if (!config?.maxRequests || !config?.interval) return true;

    const now = Date.now();
    const requestCount = this.requestCounts.get(key);

    if (!requestCount || now >= requestCount.resetTime) {
      this.requestCounts.set(key, { count: 1, resetTime: now + config.interval });
      return true;
    }

    if (requestCount.count >= config.maxRequests) {
      return false;
    }

    requestCount.count++;
    return true;
  }

  set<T>(key: string, data: T): void {
    const config = CACHE_CONFIG[key];
    if (!config) {
      console.warn(`No cache config found for key: ${key}`);
      return;
    }
    
    try {
      if (!this.updateRequestCount(key)) {
        console.warn(`Rate limit exceeded for ${key}`);
        return;
      }

      // Update memory cache
      this.memoryCache.set(key, data);
      this.lastAccess.set(key, Date.now());

      // Update persistent cache
      this.cache.set(key, {
        data,
        timestamp: Date.now(),
        version: config.version
      });
    } catch (error) {
      console.error(`Error setting cache for ${key}:`, error);
      // Clear potentially corrupted cache entry
      this.clear(key);
    }
  }

  get<T>(key: string): T | null {
    try {
      if (!this.updateRequestCount(key)) {
        console.warn(`Rate limit exceeded for ${key}`);
        return null;
      }

      // Check memory cache first
      if (this.memoryCache.has(key)) {
        this.lastAccess.set(key, Date.now());
        return this.memoryCache.get(key);
      }

      // Check persistent cache
      const entry = this.cache.get(key);
      const config = CACHE_CONFIG[key];

      if (!entry || !config) return null;

      // Check version and age
      if (entry.version !== config.version || Date.now() - entry.timestamp > config.maxAge) {
        this.clear(key);
        return null;
      }

      // Update memory cache
      this.memoryCache.set(key, entry.data);
      this.lastAccess.set(key, Date.now());

      return entry.data;
    } catch (error) {
      console.error(`Error getting cache for ${key}:`, error);
      // Clear potentially corrupted cache entry
      this.clear(key);
      return null;
    }
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
      this.memoryCache.delete(key);
      this.lastAccess.delete(key);
      this.requestCounts.delete(key);
    } else {
      this.cache.clear();
      this.memoryCache.clear();
      this.lastAccess.clear();
      this.requestCounts.clear();
    }
  }

  isStale(key: string): boolean {
    // Check memory cache first
    if (this.memoryCache.has(key)) {
      const lastAccessTime = this.lastAccess.get(key);
      if (lastAccessTime && Date.now() - lastAccessTime <= this.MEMORY_CACHE_TTL) {
        return false;
      }
    }

    const entry = this.cache.get(key);
    const config = CACHE_CONFIG[key];

    if (!entry || !config) return true;

    return (
      entry.version !== config.version ||
      Date.now() - entry.timestamp > config.maxAge
    );
  }

  getMemoryCacheStats() {
    return {
      size: this.memoryCache.size,
      keys: Array.from(this.memoryCache.keys()),
      lastAccess: Object.fromEntries(this.lastAccess)
    };
  }

  getPersistentCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      entries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        timestamp: entry.timestamp,
        version: entry.version,
        age: Date.now() - entry.timestamp
      }))
    };
  }

  getRateLimitStats() {
    return {
      limits: Object.fromEntries(
        Object.entries(CACHE_CONFIG)
          .filter(([, config]) => config.maxRequests)
          .map(([key, config]) => [key, {
            maxRequests: config.maxRequests,
            interval: config.interval,
            current: this.getRateLimitInfo(key)
          }])
      )
    };
  }
}

export const cacheManager = CacheManager.getInstance();